import React from "react";
import { Icon, ButtonLink } from "@clarkinc/zest-design-system/ui";
import NoScriptLazyImage from "~/common/NoScriptLazyImage";
import { cls } from "@wss/cls";

export interface PhotoGridItem {
  description: string;
  image: string;
  url: string;
  name: string;
  featuresPerRow: number;
  actionText: string;
  children?: PhotoGridChildItem[];
}

export interface PhotoGridChildItem {
  name: string;
  url: string;
}

interface Props {
  item: PhotoGridItem;
  showActionButton?: boolean;
  lazyLoadImage?: boolean;
}

const PhotoGridDisplay = ({
  item,
  showActionButton = true,
  lazyLoadImage = false,
}: Props) => {
  const itemUrlClasses = !item.url ? "mt-0 xxl:mt-5" : "";

  const displayImageContainer = () => {
    if (item.url) {
      return (
        <a
          href={item.url}
          className={cls(
            "img-container img-container--hover block mb-5 overflow-hidden relative",
          )}
          aria-hidden="true"
          tabIndex={-1}
        >
          <div
            className={cls(
              "h-0 m-0 relative",
              "hover:opacity-75",
              "transition ease-in-out duration-300 delay-75",
            )}
            style={{
              paddingBottom: "63.5%",
            }}
          >
            <NoScriptLazyImage
              className={cls("absolute inset-0 w-full rounded")}
              src={item.image}
              alt=""
              loading={lazyLoadImage ? "lazy" : undefined}
            />
          </div>
          {showActionButton && (
            <div
              className={cls(
                "btn btn-large img-container__btn bg-white border-solid border-2 border-white text-black",
                "hover:text-green-600",
              )}
            >
              {`Shop ${item.name}`}
            </div>
          )}
        </a>
      );
    }
    return (
      <div
        className={cls("h-0 m-0 relative block")}
        style={{ paddingBottom: "63.5%" }}
      >
        <NoScriptLazyImage
          className={cls("absolute inset-0 rounded w-full")}
          src={item.image}
          role="presentation"
          loading={lazyLoadImage ? "lazy" : undefined}
        />
      </div>
    );
  };

  const displayTitle = () => {
    if (item.url) {
      return (
        <a
          href={item.url}
          className={cls(
            "font-semibold text-sm text-center",
            "xs:text-xl xs:mb-1 xs:text-left",
            "sm:leading-snug",
          )}
        >
          <h2
            className={cls(
              "text-base leading-5 mb-0 -mt-2 text-center",
              "xs:text-xl xs:leading-5 xs:mt-0 xs:text-left",
            )}
            data-testid="displayTitle"
          >
            {item.name}
          </h2>
          <p
            className={cls(
              "flex items-center justify-center text-sm leading-4 mt-1",
              "xs:mb-2 xs:justify-start",
            )}
            data-testid="displayCategories"
          >
            {`${item.actionText} `}
            <Icon
              className={cls("h-4 w-4 fill-current m-0")}
              name="ri-arrow-right-s-line"
            />
          </p>
        </a>
      );
    }

    return (
      <h2
        className={cls(
          "text-base leading-5 mb-0 mt-3 text-center",
          "xs:text-xl xs:leading-5 xs:mb-3 xs:mt-5 xs:text-left",
        )}
        data-testid="displayTitle"
      >
        {item.name}
      </h2>
    );
  };

  return (
    <div className={cls("photo-grid-display")} data-testid="featuredColumn">
      {displayImageContainer()}
      <div
        className={cls(
          item.featuresPerRow % 2 !== 0 && item.children?.length !== 0
            ? "xxl:inline-block xxl:float-left xxl:w-2/3"
            : "",
          "text-sm leading-snug xs:pr-5",
        )}
        data-testid="grid__text"
      >
        {displayTitle()}
        <p
          className={cls("hidden break-words xs:block xs:mb-0")}
          data-testid="displayDescription"
          dangerouslySetInnerHTML={{ __html: item.description }}
        />
      </div>
      {!!item.children?.length && (
        <div
          className={cls(
            "pt-1",
            item.featuresPerRow % 2 !== 0 && item.children?.length !== 0
              ? `xxl:float-left xxl:w-1/3 ${itemUrlClasses}`
              : "",
            item.url
              ? "hidden xs:flex xs:flex-col"
              : "flex flex-col items-center xs:items-start xs:gap-0",
          )}
          data-testid="Children Categories"
        >
          {item.children?.map((member) => (
            <ButtonLink
              color="green"
              className={cls(
                "pl-0 py-1.5 text-sm font-normal leading-none justify-start h-auto",
                "hover:bg-transparent hover:underline",
                "active:bg-transparent active:shadow-none",
                !item.url && "text-center",
              )}
              data-testid="grid__grand-children"
              href={member.url}
              key={member.name}
              size="small"
              theme="wss"
              variant="tertiary"
            >
              {member.name}
            </ButtonLink>
          ))}
        </div>
      )}
    </div>
  );
};

export default PhotoGridDisplay;
